// Compare the meaning retained by the server's rich-text sanitizer, without
// rendering HTML. Entity spelling and visual styling are not author edits.
const allowed = new Set(['p','br','strong','b','em','i','u','s','ul','ol','li','h1','h2','h3','h4','h5','h6','a','table','thead','tbody','tr','th','td','blockquote','span','div','img','sup','sub','hr']);
const discard = new Set(['script','style','iframe','object','embed','noscript','textarea','title','xmp','plaintext','svg','math','template','meta','link','base']);
const blocks = new Set(['p','ul','ol','li','h1','h2','h3','h4','h5','h6','table','thead','tbody','tr','th','td','blockquote','div','hr']);
const semanticAttributes = { a:['href','title'], img:['src','alt'], td:['colspan','rowspan'], th:['colspan','rowspan'] };
const asciiSpace = /[\t\n\f\r ]+/g;
const isBlock = node => Boolean(node && typeof node !== 'string' && blocks.has(node.tag));

function safeUrl(value, attribute) {
  if ([...value].some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127) || /^[\\/]{2}/.test(value)) return false;
  try { return ['https:','http:',...(attribute === 'href' ? ['mailto:'] : [])].includes(new URL(value,'https://localhost.invalid').protocol); }
  catch { return false; }
}

export function canonicalSeoHtml(value, documentRoot = typeof document === 'undefined' ? null : document) {
  if (!documentRoot?.createElement || typeof value !== 'string' || value.length > 150000) return null;
  // Template content is inert: it is never inserted into the page, never runs
  // scripts, and does not load images/iframes or modify the user's document.
  const template = documentRoot.createElement('template');
  try { template.innerHTML = value; } catch { return null; }
  let count = 0;
  function children(parent, depth, boundary) {
    if (depth > 128) throw Error('HTML comparison nesting limit');
    const output = [];
    for (const child of parent.childNodes) {
      for (const cleaned of node(child, depth + 1)) {
        if (typeof cleaned === 'string' && typeof output[output.length - 1] === 'string') output[output.length - 1] += cleaned;
        else output.push(cleaned);
      }
    }
    return output.map((item,index) => {
      if (typeof item !== 'string') return item;
      if (/^ *$/.test(item) && (isBlock(output[index - 1]) || isBlock(output[index + 1]) || (boundary && (index === 0 || index === output.length - 1)))) return '';
      let text = item;
      if (boundary && index === 0) text = text.replace(/^ +/,'');
      if (boundary && index === output.length - 1) text = text.replace(/ +$/,'');
      return text;
    }).filter(item => item !== '');
  }
  function node(element, depth) {
    if (++count > 10000) throw Error('HTML comparison node limit');
    if (element.nodeType === 3) return [element.nodeValue.replace(asciiSpace,' ')];
    if (element.nodeType !== 1) return [];
    const tag = element.localName.toLowerCase();
    if (discard.has(tag)) return [];
    const contents = children(element,depth,blocks.has(tag));
    // An unstyled span has no retained semantic attributes. Removing this
    // presentation wrapper also handles CKEditor merging adjacent text spans.
    if (!allowed.has(tag) || tag === 'span') return contents;
    const attributes = {};
    for (const attribute of semanticAttributes[tag] || []) {
      const value = element.getAttribute(attribute);
      if (value === null) continue;
      if (['href','src'].includes(attribute) && !safeUrl(value,attribute)) continue;
      if (['colspan','rowspan'].includes(attribute) && !/^\d{1,4}$/.test(value)) continue;
      attributes[attribute] = value;
    }
    return [{ tag: tag === 'b' ? 'strong' : tag === 'i' ? 'em' : tag, attributes, children: contents }];
  }
  try { return JSON.stringify(children(template.content,0,true)); }
  catch { return null; }
}

export function equivalentSeoHtml(a, b) {
  const left = String(a || ''), right = String(b || '');
  if (left === right) return true;
  const one = canonicalSeoHtml(left), two = canonicalSeoHtml(right);
  // No DOM is available in server-side/pure Node consumers. Refuse to guess
  // semantic equality from text alone; strict equality remains conservative.
  return one !== null && two !== null ? one === two : left.trim() === right.trim();
}
